import { observable, action, makeObservable, computed } from 'mobx'
import { extractMessageFromError } from 'utils/error-util'
import {
  interval,
  Subject,
  merge,
  defer,
  Observable,
  combineLatest,
} from 'rxjs'
import { takeUntil, skipUntil, switchMap, share, take } from 'rxjs/operators'
import { shareLatest, unpair } from 'utils/rx-util'
import { v4 } from 'uuid'
import { isNotNil } from 'utils/object-util'
import { fromStream, toStream } from '../utils/mobx-util'

/**
 * Alert factory.
 */
export type AlertFactory = () => Alert

/**
 * Alert model.
 */
export class Alert {
  /**
   * Used for notifying when to cancel auto-dismiss.
   */
  private cancelAutoDismissSubject = new Subject()

  /**
   * Used for starting an Auto Dismiss timer.
   */
  private autoDismissSubject = new Subject<number>()

  /**
   * Used for notifying when to cancel auto-dismiss.
   */
  private dismissSubject = new Subject()

  /**
   * Used for notifying when to show the alert.
   */
  private showSubject = new Subject()

  /**
   * Used for notifying when an interaction with the alert has occurred.
   */
  private interactSubject = new Subject()

  /**
   * Used for triggering actions.
   */
  private actionSubject = new Subject<AlertAction>()

  /**
   * The interaction when clicking the Alert.
   */
  private interactHandler = () => {
    /* Noop as default */
  }

  /**
   * Alert ID.
   */
  readonly id = v4()

  /**
   * The alert title.
   */
  @observable _title: string | (() => string) = ''

  /**
   * The alert description.
   */
  @observable _description: string | (() => string) = ''

  /**
   * The alert state.
   */
  @observable state = AlertState.Neutral

  /**
   * Alert actions.
   */
  @observable actions: AlertAction[] = []

  /**
   * Emits when the alert is dismissed.
   */
  readonly dismissed$: Observable<unknown>

  /**
   * Holds values from RxJS streams.
   */
  private readonly $

  /**
   * Creates an Alert.
   *
   * @param onShow
   * @param onDismiss
   */
  constructor(onShow: () => unknown, onDismiss: () => unknown) {
    makeObservable(this)
    // Show the alert, but...
    const show$ = this.showSubject.pipe(
      // * never after having been dismissed
      takeUntil(defer(() => dismiss$)),
      // * and only show once
      take(1),
      share(),
    )

    // Dismiss the alert when...
    const dismiss$: Observable<unknown> = merge(
      // * explicitly told to dismiss
      this.dismissSubject,
      // * interacting with the alert
      defer(() => merge(interact$, action$)),
      // * auto-dismiss has been requested...
      //   (only start the auto dismiss timer after having been shown)
      combineLatest([this.autoDismissSubject, show$]).pipe(
        unpair(),
        switchMap((ms) =>
          // and the timer elapsed
          interval(ms).pipe(
            // but if we cancel auto dismiss, stop
            takeUntil(this.cancelAutoDismissSubject),
            // and we only care about the first emission of the interval
            take(1),
          ),
        ),
      ),
    ).pipe(
      // ... but only after having been shown.
      skipUntil(show$),
      // and only dismiss once.
      take(1),
      share(),
    )

    // Handle primary interactions while showing.
    const interact$: Observable<unknown> = this.interactSubject.pipe(
      skipUntil(show$),
      take(1),
      // Only trigger if we haven't triggered an action.
      takeUntil(defer(() => action$)),
      share(),
    )

    // Handle actions while showing.
    const action$ = this.actionSubject.pipe(
      skipUntil(show$),
      take(1),
      // Only trigger if we haven't triggered an interaction.
      takeUntil(interact$),
      share(),
    )

    show$.subscribe(onShow)
    dismiss$.subscribe(onDismiss)
    interact$.subscribe(() => this.interactHandler())
    action$.subscribe((a) => a.handler())

    this.dismissed$ = dismiss$

    // Ensures that the title doesn't change after the alert is dismissed.
    const title$ = toStream(
      () => (this._title instanceof Function ? this._title() : this._title),
      true,
    ).pipe(takeUntil(dismiss$), shareLatest())

    // Ensures that the description doesn't change after the alert is dismissed.
    const description$ = toStream(
      () =>
        this._description instanceof Function
          ? this._description()
          : this._description,
      true,
    ).pipe(takeUntil(dismiss$), shareLatest())

    this.$ = {
      title: fromStream('', title$),
      description: fromStream('', description$),
    }
  }

  /**
   * The toast's title.
   */
  @computed
  get title() {
    return this.$.title()
  }

  /**
   * The toast's description.
   */
  @computed
  get description() {
    return this.$.description()
  }

  /**
   * Changes the title and description of the alert.
   *
   * @param title the new title.
   * @param description the new description. If omitted, sets to an empty string.
   */
  @action.bound
  saying(
    title: string | (() => string),
    description?: string | (() => string),
  ): this {
    this._title = title
    this._description = description || ''

    return this
  }

  /**
   * Sets the alert state.
   */
  @action.bound
  withState(state: AlertState): this {
    this.state = state
    return this
  }

  /**
   * Sets the handler to call when interacting (clicking/touching) the Alert.
   */
  @action.bound
  onInteract(handler: () => unknown): this {
    this.interactHandler = handler
    return this
  }

  /**
   * Specifies up to 2 possible actions to take.
   * These replace the default "Close" action.
   */
  @action.bound
  withActions(primaryAction: AlertAction, secondaryAction?: AlertAction): this {
    this.actions = [primaryAction, secondaryAction].filter(isNotNil)
    return this
  }

  /**
   * Populates the alert's title and description from an error.
   *
   * @param err
   */
  @action.bound
  fromError(err: unknown) {
    const messageFromError = extractMessageFromError(err)
    const [firstPart, ...rest] = messageFromError.split(':')
    return this.withState(AlertState.Error).saying(
      firstPart.trim(),
      rest.join(':').trim(),
    )
  }

  /**
   * Schedules the alert to auto-dismiss after the specified amount of milliseconds.
   *
   * @param ms
   */
  @action.bound
  autoDismiss(ms = 5000) {
    this.autoDismissSubject.next(ms)
    return this
  }

  /**
   * Shows the built alert.
   */
  @action.bound
  show(): this {
    this.showSubject.next(undefined)
    return this
  }

  /**
   * Dismisses the alert. Once dismissed, cannot be shown again.
   */
  @action.bound
  dismiss(): this {
    this.dismissSubject.next(undefined)
    return this
  }

  /**
   * Cancels any pending auto dismiss.
   */
  @action.bound
  cancelAutoDismiss() {
    this.cancelAutoDismissSubject.next(undefined)
    return this
  }

  /**
   * Calls the interact handler and dismisses the Alert.
   * Used by the view. You probably want to use `onInteract` to set up the handler.
   */
  @action.bound
  interact(): this {
    this.interactSubject.next(undefined)
    return this
  }

  /**
   * Triggers the specified action and dismisses the Alert.
   *
   * @param alertAction
   */
  @action.bound
  triggerAction(alertAction: AlertAction): this {
    this.actionSubject.next(alertAction)
    return this
  }
}

/**
 * An Alert action.
 */
export interface AlertAction {
  /**
   * The Action title.
   */
  title: string

  /**
   * The Action handler.
   */
  handler(): unknown
}

/**
 * App alert state.
 */
export const enum AlertState {
  Neutral = 'neutral',
  Pending = 'pending',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}
